import React from 'react';
import numeral from 'numeral';
import { ProfileType } from '../../../enums/ProfileType';
import { SeparationType } from '../../../enums/SeparationType';
import { HistoryStateType } from '../../../enums/HistoryStateType';
import { strings } from '../../../resources/strings';
import { Order } from '../../../models/Order';
import { ErpOrder } from '../../../models/ErpOrder';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { HistoryByOrder } from '../../../services/Orders/GetHistoryByOrder/HistoryByOrder';
import { useAuth } from '../../../contexts/AuthContext';
import { Table } from '../../../common-components/Table/Table';
import { If } from '../../../common-components/If/If';
import { TableRow } from '../../../common-components/TableRow/TableRow';
import { TableHead } from '../../../common-components/TableHead/TableHead';
import { TableData } from '../../../common-components/TableData/TableData';
import { MultilinePopover } from '../../../common-components/MultilinePopover/MultilinePopover';
import { Container } from './styles';
import { AssemblyType } from '../../../enums/AssemblyType';

interface ITabItemsProps {
  order?: Order | null;
  erpOrder?: ErpOrder | null;
  historic?: HistoryByOrder[];
}

export const TabItems: React.FC<ITabItemsProps> = ({
  order,
  erpOrder,
  historic
}) => {

  const { session } = useAuth();

  const userProfile = session?.user?.profileType;
  const firstLocationInfo = session?.locations && session?.locations?.length > 0 ? session?.locations[0] : null;
  const onlyMakeExternalAssembly = firstLocationInfo?.assemblyType === AssemblyType.External;
  const onlyMakeInternalAssemblyWithoutAssembler = firstLocationInfo?.assemblyType === AssemblyType.InternalWithoutAssembler;

  const displayAssemblyColumn = ((userProfile === ProfileType.Vendor && !onlyMakeExternalAssembly && 
    !onlyMakeInternalAssemblyWithoutAssembler) || userProfile === ProfileType.Admin) && order?.separationType === SeparationType.Prescription;

  const items = React.useMemo(() => {

    const total = erpOrder?.totalValue || order?.orderItems?.reduce((total, item) => total + (item.quantity * item.price), 0) || 0;

    const totalRow = (
      <TableRow key={`item-total`}>
        <TableData></TableData>
        <TableData></TableData>
        <If condition={displayAssemblyColumn}>
          <TableData></TableData>
        </If>
        <TableData width='150px'></TableData>
        <TableData width='150px'></TableData>
        <TableData width='100px' textAlign='right' bold>
          {FormatHelper.formatCurrency(total)}
        </TableData>
      </TableRow>
    );

    return (
      <Table>
        <thead>
          <TableRow>
            <TableHead width='150px'>{strings.Sku}</TableHead>
            <TableHead>{strings.Name}</TableHead>
            <If condition={displayAssemblyColumn}>
              <TableHead textAlign='center' width='120px'>Montagem</TableHead>
            </If>
            <TableHead width='150px' textAlign='right'>{strings.Price}</TableHead>
            <TableHead width='150px' textAlign='right'>{strings.Quantity}</TableHead>
            <TableHead width='100px' textAlign='right'>{strings.Total}</TableHead>
          </TableRow>
        </thead>
        <tbody>

          {order?.orderItems?.map(orderItem => {
            const total = orderItem.quantity * orderItem.price;
            const itemIsNotActive = orderItem.canceledAt || orderItem.returnedAt || !orderItem.active;
            const textColor = itemIsNotActive ? 'red' : null;

            const assemblyStatus: string[] = [];
            let assemblyPopoverValue = '';
            if (displayAssemblyColumn) {
              assemblyPopoverValue = `${orderItem.assemblerReceivedConfirmedQuantity || 0}` +
                `/${orderItem.assemblyStartConfirmedQuantity || 0}` +
                `/${orderItem.assemblyEndConfirmedQuantity || 0}` +
                `/${orderItem.sentToStoreConfirmedQuantity || 0}`;

              const assemblerReceivedConfirmedStatus = historic?.filter(h => h.stateType === HistoryStateType.OrderStateLog &&
                h.details?.startsWith('[Montagem]') &&
                h.details?.includes('recebido por') &&
                h.details?.includes(`Item: ${orderItem.sku} - ${orderItem.name}`)
              )?.map(h => h.userName + ' ' + FormatHelper.formatDateTime(h.createdAt)) || [];
              const assemblyStartConfirmedStatus = historic?.filter(h => h.stateType === HistoryStateType.OrderStateLog &&
                h.details?.startsWith('[Montagem]') &&
                h.details?.includes('iniciado por') &&
                h.details?.includes(`Item: ${orderItem.sku} - ${orderItem.name}`)
              )?.map(h => h.userName + ' ' + FormatHelper.formatDateTime(h.createdAt)) || [];
              const assemblyEndConfirmedStatus = historic?.filter(h => h.stateType === HistoryStateType.OrderStateLog &&
                h.details?.startsWith('[Montagem]') &&
                h.details?.includes('concluído por') &&
                h.details?.includes(`Item: ${orderItem.sku} - ${orderItem.name}`)
              )?.map(h => h.userName + ' ' + FormatHelper.formatDateTime(h.createdAt)) || [];
              const sentToStoreConfirmedStatus = historic?.filter(h => h.stateType === HistoryStateType.OrderStateLog &&
                h.details?.startsWith('[Montagem]') &&
                h.details?.includes('enviado para loja por') &&
                h.details?.includes(`Item: ${orderItem.sku} - ${orderItem.name}`)
              )?.map(h => h.userName + ' ' + FormatHelper.formatDateTime(h.createdAt)) || [];

              assemblyStatus.push(`Recebido: ${orderItem.assemblerReceivedConfirmedQuantity || 0}`);
              assemblyStatus.push(...assemblerReceivedConfirmedStatus);
              assemblyStatus.push(`---------------------------`);

              assemblyStatus.push(`Montando: ${orderItem.assemblyStartConfirmedQuantity || 0}`);
              assemblyStatus.push(...assemblyStartConfirmedStatus);
              assemblyStatus.push(`---------------------------`);

              assemblyStatus.push(`Montado: ${orderItem.assemblyEndConfirmedQuantity || 0}`);
              assemblyStatus.push(...assemblyEndConfirmedStatus);
              assemblyStatus.push(`---------------------------`);

              assemblyStatus.push(`Enviado Loja: ${orderItem.sentToStoreConfirmedQuantity || 0}`);
              assemblyStatus.push(...sentToStoreConfirmedStatus);
            }

            const isInputProduct = orderItem.title?.toLowerCase().includes('insumo');

            return (
              <TableRow key={`item-${orderItem.id}`}>
                <TableData textColor={textColor} data-id={orderItem.id}>{orderItem.sku}</TableData>
                <TableData textColor={textColor}>{orderItem.title}{orderItem.variantTitle ? ` - ${orderItem.variantTitle}` : ''}</TableData>
                <If condition={displayAssemblyColumn}>
                  <TableData textColor={textColor} textAlign='center'>
                    <If condition={isInputProduct}>
                      <MultilinePopover
                        keyid={`item-${orderItem.id}`}
                        bagdeValue={'N/A'}
                        marginLeft='0px'
                        bagdeBackgroundColor='#fff'
                        bagdeTextColor='#777'
                        bagdeWidth='85px'
                        lines={['Não se aplica']}
                      />
                    </If>
                    <If condition={!isInputProduct}>
                      <MultilinePopover
                        keyid={`item-${orderItem.id}`}
                        bagdeValue={assemblyPopoverValue}
                        marginLeft='0px'
                        bagdeBackgroundColor='#bbb'
                        bagdeTextColor='#fff'
                        bagdeWidth='85px'
                        lines={assemblyStatus}
                      />
                    </If>
                  </TableData>
                </If>
                <TableData width='150px' textAlign='right' textColor={textColor}>{numeral(orderItem.price).format('0,0.00')}</TableData>
                <TableData width='150px' textAlign='right' textColor={textColor}>{orderItem.quantity}</TableData>
                <TableData width='100px' textAlign='right' textColor={textColor}>{numeral(total).format('0,0.00')}</TableData>
              </TableRow>
            );
          })}
          {totalRow}
        </tbody>
      </Table>
    );
  }, [order, historic]);

  return (
    <Container>
      {items}
    </Container>
  );

};